import { Component } from '@theme/component';

const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const STICKY_OFFSET_PROPERTY = '--sticky-header-mobile-offset';
const HEADER_SELECTOR = 'header-component';
const PRODUCT_GRID_SELECTOR = 'ul.product-grid';
const FALLBACK_OFFSET = '0px';

/**
 * Facets Sticky Toggle
 *
 * Tracks the current rendered height of the site `<header-component>` and
 * exposes it as the CSS custom property `--sticky-header-mobile-offset` on
 * itself. The `.facets-toggle` uses this variable as its `top` value when it
 * becomes `position: sticky` on mobile viewports, so it always sits flush
 * beneath whatever header is currently pinned.
 *
 * The observer only runs on mobile viewports (<= 749px). On larger viewports
 * the variable is cleared so the sticky behavior is effectively disabled and
 * desktop layout stays untouched.
 *
 * Additionally, a MutationObserver watches `ul.product-grid` to detect
 * Shopify section renders (e.g. filter apply) and re-runs the sync, since the
 * morph may have swapped the `<header-component>` node as well.
 *
 * @extends {Component}
 */
class FacetsStickyToggle extends Component {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #productGridObserver = null;

  /** @type {MediaQueryList} */
  #mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback?.();

    this.#abortController = new AbortController();
    this.#mobileMediaQuery.addEventListener('change', this.#syncOffset, {
      signal: this.#abortController.signal,
    });

    this.#syncOffset();
    this.#observeProductGrid();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#abortController?.abort();
    this.#abortController = null;
    this.#disconnectResizeObserver();
    this.#disconnectProductGridObserver();
    this.style.removeProperty(STICKY_OFFSET_PROPERTY);
  }

  /**
   * Entry point that decides whether to observe the header or to clean up,
   * based on the current viewport. Bound as a class field so it can be used
   * directly as an event listener / observer callback.
   */
  #syncOffset = () => {
    if (!this.#mobileMediaQuery.matches) {
      this.#disconnectResizeObserver();
      this.style.removeProperty(STICKY_OFFSET_PROPERTY);
      return;
    }

    this.#observeHeader();
  };

  /**
   * Locates the `<header-component>` element and attaches a ResizeObserver so
   * any change in its rendered height (transparent header collapse, translate
   * transforms, announcement bar toggles) is reflected in the CSS variable.
   *
   * The observer is always rebuilt, because after a section morph the previous
   * header-component node may have been replaced and the old observer would
   * be pointing at a detached element.
   */
  #observeHeader() {
    this.#disconnectResizeObserver();

    const header = document.querySelector(HEADER_SELECTOR);

    if (!(header instanceof HTMLElement)) {
      this.style.setProperty(STICKY_OFFSET_PROPERTY, FALLBACK_OFFSET);
      return;
    }

    this.#writeOffset(header);
    this.#resizeObserver = new ResizeObserver(() => this.#writeOffset(header));
    this.#resizeObserver.observe(header);
  }

  /**
   * Observes the product grid `<ul>` for DOM mutations so we can re-sync the
   * sticky offset every time the collection section is morphed (filter apply,
   * infinite scroll replace, etc.). If the markup is not yet available, we
   * retry once on the next animation frame.
   */
  #observeProductGrid() {
    this.#disconnectProductGridObserver();

    const productGrid = this.#findProductGrid();

    if (productGrid) {
      this.#attachProductGridObserver(productGrid);
      return;
    }

    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      const retry = this.#findProductGrid();
      if (retry) this.#attachProductGridObserver(retry);
    });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #findProductGrid() {
    const productGrid = document.querySelector(PRODUCT_GRID_SELECTOR);
    return productGrid instanceof HTMLElement ? productGrid : null;
  }

  /**
   * @param {HTMLElement} productGrid
   */
  #attachProductGridObserver(productGrid) {
    this.#productGridObserver = new MutationObserver(this.#syncOffset);
    this.#productGridObserver.observe(productGrid, { childList: true });
  }

  #disconnectResizeObserver() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #disconnectProductGridObserver() {
    this.#productGridObserver?.disconnect();
    this.#productGridObserver = null;
  }

  /**
   * Writes the current header height into the CSS custom property. The `-1`
   * compensates for sub-pixel rounding that would otherwise leave a hairline
   * gap between the pinned header and the sticky facets toggle.
   *
   * @param {HTMLElement} header
   */
  #writeOffset(header) {
    const height = Math.ceil(header.getBoundingClientRect().height - 1);
    this.style.setProperty(STICKY_OFFSET_PROPERTY, `${height}px`);
  }
}

if (!customElements.get('facets-sticky-toggle')) {
  customElements.define('facets-sticky-toggle', FacetsStickyToggle);
}
